import Vue from 'vue'
import lodash from 'lodash'
import * as companiesApi from '@/api/companies/request'
import types from '@/store/mutation-types'
import { forceUpdateVueObject } from '@/store/util'
import * as contactsApi from '@/api/ccdcontacts/request'
import * as tenanciesApi from '@/api/tenancies/request'
import * as leasesApi from '@/api/leases/request'
import { displayI18nFullName } from '@/utils/fullName'
import { companyType } from '@/utils/locale'
import { EmailTypes } from '@/utils/consts'
import { sendEmail } from '@/api/postman/request'
import { setPhysicalSite } from '@/utils/siteUtils'
import { utils as formUtils } from '@/plugin/InputChange'
import { aclHelper, contractorPermsTypes } from '@/utils/aclHelper'
import { getActiveAndFutureFloors } from '@/modules/ccd/tenancy/utils'
import { TENANCY_ALL_CONTACT_TYPES } from '@/modules/ccd/tenancy/consts'

function removeContactFromCompany (ccdcontacts, index) {
  ccdcontacts.splice(index, 1)
}

function addLeaseToTenancy (leases, lease) {
  leases.push(lease)
}

function removeLeaseFromTenancy (leases, index) {
  leases.splice(index, 1)
}

function sanitizeTenancySections (request) {
  if (Array.isArray(request.sections)) {
    request.sections = request.sections.filter(section => section && section !== '--')
  }
}

const defaultState = getDefaultState()

function resetCurrentContact () {
  return {
    phoneNumbers: [],
    faxNumbers: [],
    emails: [],
    addresses: [],
    firstName: '',
    lastName: '',
    active: 1,
  }
}

function getDefaultState () {
  return {
    currentCompany: {},
    currentContact: resetCurrentContact(),
    currentTenancy: {},
    searchItems: {},
    searchResults: [],
    performanceSearchConditions: {},
    performanceSearchResults: [],
    accounts: [],
    eCircularSearchCount: 0,
    lastSearchedECirculars: [],
    lastSearchedCategories: [],
    lastECircularSearchAttrs: {},
    eCircularFilterConditions: {},
    eCircularTableOrder: {},
    searchTotal: 0,
  }
}

const getters = {
  currentCompany: state => state.currentCompany,
  currentContact: state => state.currentContact,
  currentTenancy: state => state.currentTenancy,
  getSearchItems: state => state.searchItems,
  getSearchResults: state => state.searchResults,
  searchTotal: state => state.searchTotal,
  performanceSearchConditions: state => state.performanceSearchConditions,
  performanceSearchResults: state => state.performanceSearchResults,
  accounts: state => lodash.get(state, 'currentTenancy.accounts', []),
  lastECircularSearchAttrs: state => state.lastECircularSearchAttrs,
  lastSearchedECirculars: state => state.lastSearchedECirculars,
  lastSearchedCategories: state => state.lastSearchedCategories,
  eCircularSearchCount: state => state.eCircularSearchCount,
  eCircularFilterConditions: state => state.eCircularFilterConditions,
  eCircularTableOrder: state => state.eCircularTableOrder,
  currentTenancyFloors: state => {
    const allFloors = []
    const premisesDetailList = lodash.get(state, 'currentTenancy.premisesDetailList', [])
    premisesDetailList.map(item => {
      allFloors.push(item.floor)
    })
    return Array.from(new Set(allFloors))
  },
  premisesDetailList: state => {
    return lodash.get(state, 'currentTenancy.premisesDetailList', [])
  },
  isMaster: (state, getters, rootState, rootGetters) => {
    const user = rootGetters['user/currentUser'] || {}
    const result = getters.accounts
      .find((account) => account.TenancyAccount.isAdmin && user.id === account.id)
    return !!result
  },
  isSubAdmin: (state, getters, rootState, rootGetters) => {
    const user = rootGetters['user/currentUser'] || {}
    const result = getters.accounts
      .find((account) => account.TenancyAccount.perms.SubAdmin && user.id === account.id)
    return !!result
  },
  canAccessAllProjects: (state, getters, rootState, rootGetters) => {
    const user = rootGetters['user/currentUser'] || {}
    const selfAccount = getters.accounts.filter(account => account.id === user.id)[0] || {}
    return getters.isMaster || !!lodash.get(selfAccount, 'TenancyAccount.perms.TenantProject', false)
  },
  canAccessAllHotbs: (state, getters, rootState, rootGetters) => {
    const user = rootGetters['user/currentUser'] || {}
    const selfAccount = getters.accounts.filter(account => account.id === user.id)[0] || {}
    return getters.isMaster || !!lodash.get(selfAccount, 'TenancyAccount.perms.HandoverTakeback', false)
  },
  canTenantEditContact: (state, getters, rootState, rootGetters) => {
    const user = rootGetters['user/currentUser'] || {}
    const selfAccount = getters.accounts.filter(account => account.id === user.id)[0] || {}
    return getters.isMaster || !!lodash.get(selfAccount, 'TenancyAccount.perms.TenancyRecord', false)
  },
  canVenodrCreateUpadteContact: (state, getters, rootState, rootGetters) => {
    const isContractorAdmin = rootGetters['user/isContractorAdmin'] || false
    return isContractorAdmin || aclHelper.getAccess(contractorPermsTypes.settings.updateVendorContacts)
  },
}

async function getCompany (companyId) {
  if (!companyId) {
    throw new Error({ message: `invalid param: ${companyId}` })
  }
  const company = await companiesApi.getCompany(companyId)
  return company
}

async function getContact (contactId) {
  if (!contactId) {
    throw new Error({ message: `invalid param: ${contactId}` })
  }
  const contact = await contactsApi.getContact(contactId)
  if (contact.firstName && contact.lastName) {
    contact.name = displayI18nFullName(contact.firstName, contact.lastName)
  }
  return contact
}

async function getCompanyLeases (companyId) {
  if (!companyId) {
    throw new Error({ message: `invalid param: ${companyId}` })
  }
  const leases = await leasesApi.getCompanyLeases(companyId)
  return leases
}

const actions = {
  async loadCompany ({ commit }, companyId) {
    const company = await getCompany(companyId)
    commit(types.COMPANY_LOAD_CURRENT, { company })
  },
  async updateCompany ({ state, commit }, { companyId, request }) {
    await companiesApi.updateCompany(companyId, JSON.parse(JSON.stringify(request)))
    // commit(types.COMPANY_UPDATE_CURRENT, { company })
  },
  async createCompany ({ state, commit }) {
    const request = JSON.parse(JSON.stringify(state.currentCompany))
    const company = await companiesApi.createCompany(request)
    commit(types.COMPANY_CREATE, { company })
  },
  async linkContactsToCompany ({ state, commit }, { companyId, request }) {
    await companiesApi.linkContactsToCompany(companyId, request)
  },
  async updateCompanyContacts ({ state, commit }, { companyId, request }) {
    await companiesApi.updateCompanyContacts(companyId, request)
  },
  async removeCompanyContacts ({ state, commit }, { companyId, request }) {
    await companiesApi.removeContactsFromCompany(companyId, request)
  },
  async loadContact ({ commit }, contactId) {
    const contact = await getContact(contactId) || {}
    contact.phoneNumbers = contact.phoneNumbers || []
    contact.faxNumbers = contact.faxNumbers || []
    contact.emails = contact.emails || []
    contact.addresses = contact.addresses || []
    commit(types.CONTACT_LOAD_CURRENT, { contact })
  },
  async updateContact ({ state, commit }, { contactId, request }) {
    const success = await contactsApi.updateContact(contactId, request)
    return success
  },
  async createContact ({ state }, { companyContact }) {
    const ccdContact = JSON.parse(JSON.stringify(state.currentContact))
    const request = {
      ccdContact,
      companyContact,
    }
    return contactsApi.createContact(request)
  },
  async confirmContacts ({ state, commit, rootState, rootGetters }) {
    const currentUser = rootGetters['user/currentUser']
    const currentTenancy = state.currentTenancy
    try {
      const result = await tenanciesApi.confirmContact(currentTenancy.id)
      commit(types.TENANCY_UPDATE_PATH_FIELD, { path: ['confirmor'], newValue: lodash.cloneDeep(currentUser) })
      commit(types.TENANCY_UPDATE_PATH_FIELD, { path: ['confirmedBy'], newValue: result.confirmedBy })
      commit(types.TENANCY_UPDATE_PATH_FIELD, { path: ['confirmedAt'], newValue: result.confirmedAt })
    } catch (error) {}
  },
  async addTenancyContactsToStore ({state, commit}, { type, ccdContactIds }) {
    const validType = TENANCY_ALL_CONTACT_TYPES.includes(type)
    if (validType && Array.isArray(ccdContactIds) && ccdContactIds.length > 0) {
      const ccdContacts = await contactsApi.listContacts({ id: ccdContactIds })
      const tenancyContacts = lodash.cloneDeep(deep(state, 'currentTenancy.tenancyContacts'))

      ccdContactIds.forEach(ccdContactId => {
        const ccdContact = ccdContacts.find(item => item.id === ccdContactId)
        const tenancyContact = tenancyContacts.find(contact => contact.id === ccdContactId)
        if (tenancyContact) {
          const cEmails = lodash.get(tenancyContact, 'CompanyContact.cEmails', [])
          if (type === 'isCircular' && !cEmails.length) {
            const allEmails = lodash.get(tenancyContact, 'allEmails', [])
            lodash.set(tenancyContact, 'CompanyContact.cEmails', allEmails)
            const floors = getActiveAndFutureFloors(state.currentTenancy)
            if (floors.length) {
              lodash.set(tenancyContact, 'CompanyContact.cFloor', floors)
            }
          }
          const mEmails = lodash.get(tenancyContact, 'CompanyContact.mEmails', [])

          if (type === 'isMarketing' && !mEmails.length) {
            const primaryEmail = lodash.get(tenancyContact, 'email')
            lodash.set(tenancyContact, 'CompanyContact.mEmails', primaryEmail ? [primaryEmail] : [])
          }
          tenancyContact.CompanyContact[type] = true
          tenancyContact.CompanyContact.isOthers = true
        } else {
          const cEmails = [lodash.get(ccdContact, ['allEmails', 0])].filter(email => !!email)
          const floors = getActiveAndFutureFloors(state.currentTenancy)
          const toAdd = {
            ...ccdContact,
            CompanyContact: {
              [type]: true,
              ccdContactId,
              companyId: lodash.get(state, 'currentTenancy.company.id'),
              cEmails: cEmails,
              cFloor: type === 'isCircular' && floors.length ? floors : [],
              nonWorkingHourOrder: null,
              workingHourOrder: null,
              oRemarks: null,
              mEmails: [],
              roleType: [],
            },
          }
          toAdd.CompanyContact.isOthers = true
          tenancyContacts.push(toAdd)
        }
      })

      const firstCircular = tenancyContacts.find(item => deep(item, 'CompanyContact.isCircular'))
      if (firstCircular && !tenancyContacts.some(item => deep(item, 'CompanyContact.cPrimary'))) {
        firstCircular.CompanyContact.cPrimary = true
      }

      commit(types.TENANCY_UPDATE_PATH_FIELD, { path: ['tenancyContacts'], newValue: tenancyContacts })
      formUtils.setDirty()
    }
  },
  async setTenancyPhysicalSite ({ commit, state }, tenancy) {
    const building = lodash.get(tenancy, 'building', {})
    let result = tenancy
    if (building.id) {
      result = await setPhysicalSite(building.id, tenancy)
    }
    return result
  },
  async loadTenancy ({ commit, dispatch }, tenancyId) {
    const tenancy = await tenanciesApi.getTenancy(tenancyId)
    if (tenancy) {
      const result = await dispatch('setTenancyPhysicalSite', tenancy)
      commit(types.TENANCY_LOAD_CURRENT, { tenancy: result })
    }
  },
  async createTenancy ({ state, commit }) {
    let request = JSON.parse(JSON.stringify(state.currentTenancy))
    sanitizeTenancySections(request)
    const isDummyCompany = deep(request, 'company.type') === companyType.D
    const storeCompanyId = state.currentCompany && state.currentCompany.id
    if (!request.companyId && !isDummyCompany && storeCompanyId) {
      request.companyId = storeCompanyId
    }
    const tenancy = await tenanciesApi.createTenancy(request)
    if (tenancy && tenancy.id) {
      commit(types.TENANCY_CREATE, { tenancy })
      sendEmail(EmailTypes.tenancies.create)
    }
    return tenancy
  },
  async updateTenancy ({ state, commit }) {
    const request = JSON.parse(JSON.stringify(state.currentTenancy))
    sanitizeTenancySections(request)
    delete request.building
    const success = await tenanciesApi.updateTenancy(state.currentTenancy.id, request)
    if (state.currentTenancy.companyChangeNotSaved) {
      commit(types.TENANCY_UPDATE_PATH_FIELD, { path: ['companyChangeNotSaved'], newValue: false })
    }
    return success
  },
  async updateTenancyStatus ({ state, commit }, tenancyStatus) {
    const success = await tenanciesApi.updateTenancyStatus(state.currentTenancy.id, tenancyStatus.toLowerCase())
    return success
  },
  async linkContactsToTenancy ({ state, commit }, { tenancyId, request }) {
    await tenanciesApi.updateTenancyContacts(tenancyId, request)
  },
  async updateTenancyContacts ({ state, commit }, { tenancyId, request }) {
    await tenanciesApi.updateTenancyContacts(tenancyId, request)
  },
  async updateOpeningHours ({ state, commit }, { tenancyId, request, isSilentUpdate }) {
    await tenanciesApi.updateOpeningHours(tenancyId, request, isSilentUpdate)
  },
  async getOpeningHours ({ state, commit }, { tenancyId, request }) {
    const openingHours = await tenanciesApi.getOpeningHours(tenancyId, request)
    commit(types.TENANCY_UPDATE_PATH_FIELD, { path: ['openingHours'], newValue: openingHours })
  },
  async getCompanyAllLeases ({ state }, { companyId }) {
    await getCompanyLeases(companyId)
  },
  async linkLeasesToTenancy ({ state, commit }, { tenancyId, request }) {
    await tenanciesApi.linkLeasesToTenancy(tenancyId, request)
  },
  async removeTenancyLeases ({ state, commit }, { tenancyId, request }) {
    await tenanciesApi.removeLeasesFromTenancy(tenancyId, request)
  },
  async uploadTenancySubAccounts ({ state }, { tenancyId, accounts }) {
    await tenanciesApi.uploadTenancySubAccounts(tenancyId, accounts)
  },
  async uploadTenancyMasterAccount ({ state }, { tenancyId, account }) {
    await tenanciesApi.uploadTenancyMasterAccount(tenancyId, account)
  },
  async setECircularSearchResults ({commit}, searchItems) {
    commit(types.TR_ECIRCULAR_SEARCH_RESULTS, searchItems)
  },
}

const mutations = {
  [types.COMPANY_LOAD_CURRENT] (state, { company }) {
    state.currentCompany = company
  },

  [types.COMPANY_UPDATE_CURRENT] (state, { company }) {
    state.currentCompany = company
  },
  [types.COMPANY_UPDATE_PATH_FIELD] (state, { path, newValue }) {
    forceUpdateVueObject(state.currentCompany, path, newValue)
  },
  [types.COMPANY_CONTACT_UPDATE_PATH_FIELD] (state, { index, path, newValue }) {
    if (deep(state, 'currentCompany.ccdcontacts') && state.currentCompany.ccdcontacts[index] &&
      state.currentCompany.ccdcontacts[index].CompanyContact) {
      const fullPath = ['ccdcontacts', index, 'CompanyContact', ...path]
      forceUpdateVueObject(state.currentCompany, fullPath, newValue)
    }
  },
  [types.COMPANY_CREATE] (state, { company }) {
    state.currentCompany = company
  },
  [types.COMPANY_REMOVE_CONTACT] (state, { index }) {
    removeContactFromCompany(state.currentCompany.ccdcontacts, index)
  },
  [types.CONTACT_LOAD_CURRENT] (state, { contact }) {
    state.currentContact = contact
  },
  [types.CONTACT_UPDATE_CURRENT] (state, { contact }) {
    state.currentContact = contact
  },
  [types.CONTACT_RESET_CURRENT] (state) {
    state.currentContact = resetCurrentContact()
  },
  [types.CONTACT_UPDATE_PATH_FIELD] (state, { path, newValue }) {
    forceUpdateVueObject(state.currentContact, path, newValue)
  },
  [types.CONTACT_CREATE] (state, { contact }) {
    state.currentContact = contact
  },
  [types.CONTACT_UPDATE_PATH_ARRAY_FIELD] (state, {arrayName, index, path, newValue}) {
    const fullPath = [arrayName, index, ...path]
    forceUpdateVueObject(state.currentContact, fullPath, newValue)
  },
  [types.CONTACT_PUSH_ITEM_TO_ARRAY] (state, { arrayName, newValue }) {
    if (Array.isArray(newValue)) {
      state.currentContact[arrayName] = newValue
    }
  },
  [types.CONTACT_REMOVE_COMPANY] (state, { index }) {
    state.currentContact.companies.splice(index, 1)
  },
  [types.RESET_STATE] (state) {
    const stateVal = getDefaultState()
    for (const field in stateVal) {
      Vue.set(state, field, stateVal[field])
    }
  },
  [types.ADVANCE_SEARCH_SET_SEARCH_CONDITIONS] (state, conditions) {
    state.searchItems = conditions
  },
  [types.ADVANCE_SEARCH_SET_SEARCH_RESULTS] (state, results) {
    state.searchResults = results
  },
  [types.ADVANCE_SEARCH_SET_SEARCH_TOTAL] (state, total) {
    state.searchTotal = total
  },
  [types.TENANCY_LOAD_CURRENT] (state, { tenancy }) {
    state.currentTenancy = tenancy
  },
  [types.TENANCY_CREATE] (state, { tenancy }) {
    state.currentTenancy = tenancy
  },
  [types.TENANCY_UPDATE_CURRENT] (state, { tenancy }) {
    state.currentTenancy = tenancy
  },
  [types.TENANCY_UPDATE_PATH_FIELD] (state, { path, newValue }) {
    forceUpdateVueObject(state.currentTenancy, path, newValue)
  },
  // Single-line body: Istanbul/cobertura line mapping for diff-cover (see .cursor/skills/pms-client-spec).
  [types.TENANCY_RESET_CURRENT] (state, { type }) { state.currentTenancy = { type, premisesDetailList: [{ detail: '', floor: '', effectiveDate: '', endDate: '' }] } },
  [types.TENANCY_CONTACT_UPDATE_PATH_FIELD] (state, { index, path, newValue }) {
    if (deep(state, 'currentTenancy.tenancyContacts') && state.currentTenancy.tenancyContacts[index] &&
      state.currentTenancy.tenancyContacts[index].CompanyContact) {
      const fullPath = ['tenancyContacts', index, 'CompanyContact', ...path]
      forceUpdateVueObject(state.currentTenancy, fullPath, newValue)
    }
  },
  [types.TENANCY_CONTACT_UPDATE_EMERGENCY_POSITION] (state, { positionsToUpdate = {} }) {
    positionsToUpdate.forEach(item => {
      state.currentTenancy.tenancyContacts[item.index].CompanyContact.workingHourOrder = item.workingHourOrder
      state.currentTenancy.tenancyContacts[item.index].CompanyContact.nonWorkingHourOrder = item.nonWorkingHourOrder
    })
  },
  [types.TENANCY_ADD_LEASE] (state, { item }) {
    addLeaseToTenancy(state.currentTenancy.leases, item)
  },
  [types.TENANCY_REMOVE_LEASE] (state, { index }) {
    removeLeaseFromTenancy(state.currentTenancy.leases, index)
  },
  [types.TENANCY_UPDATE_COMPANY_LEASE] (state, { index, path, newValue }) {
    if (deep(state, ['currentTenancy', 'company', 'leases', index])) {
      const fullPath = ['company', 'leases', index, ...path]
      forceUpdateVueObject(state.currentTenancy, fullPath, newValue)
    }
  },
  [types.TENANCY_REMOVE_ALL_LEASES] (state) {
    state.currentTenancy.leases = []
    const fullPath = ['currentTenancy', 'company', 'leases']
    const companyLeases = deep(state, fullPath)
    if (companyLeases) {
      Object.values(companyLeases).forEach(lease => {
        if (lease.tenancyId === state.currentTenancy.id) {
          lease.tenancyId = null
        }
      })
      forceUpdateVueObject(state, fullPath, companyLeases)
      forceUpdateVueObject(state.currentTenancy, ['premises'], '')
    }
  },
  [types.PERFORMANCE_SEARCH_CONDITIONS] (state, { searchConditions }) {
    Object.entries(searchConditions).forEach(([field, value]) => {
      forceUpdateVueObject(state.performanceSearchConditions, [field], value)
    })
  },
  [types.PERFORMANCE_SEARCH_RESULTS] (state, { searchResults }) {
    forceUpdateVueObject(state, ['performanceSearchResults'], searchResults)
  },
  [types.TENANCY_UPDATE_ACCOUNTS] (state, { accounts }) {
    forceUpdateVueObject(state.currentTenancy, ['accounts'], accounts)
  },
  [types.TENANCY_UPDATE_OPENING_HOURS] (state, { path, newValue }) {
    forceUpdateVueObject(state.currentTenancy, ['openingHours', path], newValue)
  },
  [types.TR_ECIRCULAR_SEARCH_RESULTS] (state, data) {
    state.lastECircularSearchAttrs = data.lastECircularSearchAttrs
    state.lastSearchedECirculars = data.lastSearchedECirculars
    state.lastSearchedCategories = data.lastSearchedCategories
  },
  [types.TR_ECIRCULAR_SAVE_SEARCH_COUNT] (state, number) {
    state.eCircularSearchCount = number
  },
  [types.TR_ECIRCULAR_SAVE_ECIRCULAR_FILTER_CONDITIONS] (state, conditions) {
    state.eCircularFilterConditions = conditions
  },
  [types.TR_ECIRCULAR_SAVE_ECIRCULAR_TABLE_ORDER] (state, order) {
    state.eCircularTableOrder = order
  },
}

export default {
  namespaced: true,
  state: defaultState,
  getters,
  actions,
  mutations,
}
